import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Paintbrush, Home, X, Expand } from "lucide-react";
import { INTERIOR_PHOTOS, EXTERIOR_PHOTOS } from "@/data";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GalleryModal({ service, labels, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md overflow-y-auto"
      data-testid="gallery-modal"
    >
      <div
        className="min-h-screen px-6 md:px-10 py-16"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="max-w-[1300px] mx-auto">
          <div className="flex items-start justify-between mb-10">
            <div>
              <span className="text-[#d4af37] uppercase tracking-[0.3em] text-xs">{labels.gallery}</span>
              <h3 className="mt-3 font-serif-lux font-light text-4xl sm:text-5xl text-white">{service.title}</h3>
              <p className="text-white/50 mt-2">{service.imgs.length} {labels.projects}</p>
            </div>
            <button
              onClick={onClose}
              data-testid="gallery-close"
              className="flex items-center gap-2 border border-[#d4af37]/40 text-[#d4af37] px-4 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-[#d4af37] hover:text-black transition-colors duration-300"
            >
              {labels.close} <X size={16} />
            </button>
          </div>

          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
            {service.imgs.map((img, i) => (
              <motion.div
                key={img}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="mb-5 break-inside-avoid overflow-hidden border border-[#d4af37]/15 group"
              >
                <img
                  src={img}
                  alt={`${service.title} project ${i + 1}`}
                  loading="lazy"
                  className="w-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Services() {
  const { t } = useLang();
  const [gallery, setGallery] = useState(null);
  const [interiorImgs, setInteriorImgs] = useState(INTERIOR_PHOTOS);
  const [exteriorImgs, setExteriorImgs] = useState(EXTERIOR_PHOTOS);

  useEffect(() => {
    let active = true;
    Promise.all([
      axios.get(`${API}/gallery/interior`).then((r) => r.data).catch(() => []),
      axios.get(`${API}/gallery/exterior`).then((r) => r.data).catch(() => []),
    ]).then(([inter, exter]) => {
      if (!active) return;
      if (inter?.length) setInteriorImgs(inter.map((x) => x.image));
      if (exter?.length) setExteriorImgs(exter.map((x) => x.image));
    });
    return () => { active = false; };
  }, []);

  const SERVICES = [
    {
      key: "interior",
      title: t.services.interior.title,
      icon: Home,
      cover: interiorImgs[0],
      imgs: interiorImgs,
      span: "md:col-span-7",
      tall: "min-h-[460px]",
      desc: t.services.interior.desc,
      tags: t.services.interior.tags,
    },
    {
      key: "exterior",
      title: t.services.exterior.title,
      icon: Paintbrush,
      cover: exteriorImgs[0],
      imgs: exteriorImgs,
      span: "md:col-span-5",
      tall: "min-h-[460px]",
      desc: t.services.exterior.desc,
      tags: t.services.exterior.tags,
    },
  ];

  const galleryLabels = {
    gallery: t.services.gallery,
    projects: t.services.projects,
    close: t.services.close,
  };

  return (
    <section id="services" className="relative py-28 md:py-40 bg-[#050505]" data-testid="services-section">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="mb-16">
          <span className="text-[#d4af37] uppercase tracking-[0.3em] text-xs flex items-center gap-3">
            <span className="h-px w-10 bg-[#d4af37]" /> {t.services.mvEyebrow}
          </span>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            <div>
              <h3 className="font-serif-lux font-light text-3xl sm:text-4xl text-white">
                {t.services.missionTitle}
              </h3>
              <p className="mt-4 text-white/65 font-light text-lg leading-relaxed max-w-xl">
                {t.services.mission}
              </p>
            </div>
            <div className="md:border-l md:border-[#d4af37]/20 md:pl-12">
              <h3 className="font-serif-lux font-light text-3xl sm:text-4xl text-[#d4af37]">
                {t.services.visionTitle}
              </h3>
              <p className="mt-4 text-white/65 font-light text-lg leading-relaxed max-w-xl">
                {t.services.vision}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {SERVICES.map((s, i) => (
            <motion.button
              key={s.key}
              type="button"
              onClick={() => setGallery(s)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative overflow-hidden border border-[#d4af37]/20 text-left cursor-pointer ${s.span} ${s.tall}`}
              data-testid={`service-card-${i}`}
              aria-label={s.title}
            >
              <img
                src={s.cover}
                alt={s.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700 ease-out"
              />
              <div className="absolute inset-0 z-10 bg-gradient-to-t from-black via-black/60 to-black/20 pointer-events-none" />
              <div className="relative z-10 h-full flex flex-col justify-between p-8 md:p-10 pointer-events-none">
                <div className="flex items-start justify-between">
                  <s.icon className="text-[#d4af37]" size={34} strokeWidth={1.2} />
                  <span className="flex items-center gap-1.5 text-white/60 group-hover:text-[#d4af37] transition-colors duration-400 text-xs uppercase tracking-[0.15em]">
                    {t.services.viewGallery} <Expand size={16} />
                  </span>
                </div>
                <div>
                  <h3 className="font-serif-lux font-light text-3xl md:text-4xl text-white">{s.title}</h3>
                  <p className="mt-3 text-white/65 font-light max-w-lg">{s.desc}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {s.tags.map((tag) => (
                      <span key={tag} className="text-[11px] uppercase tracking-[0.12em] border border-[#d4af37]/30 text-[#d4af37]/90 px-3 py-1">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {gallery && <GalleryModal service={gallery} labels={galleryLabels} onClose={() => setGallery(null)} />}
      </AnimatePresence>
    </section>
  );
}
